import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import PaginationControls from "../../components/PaginationControls";
import useDarkMode from "../../hooks/useDarkMode";
import { usePagination } from "../../hooks/usePagination";
import { getChartTheme } from "../../utils/chartTheme";
import {
  roundUp,
  toNumber,
  formatPercent,
  formatRank,
  formatCurrencyShort,
  formatCurrencyPlain,
  parseSeries,
  parseDateValue,
  formatLabel,
  formatYearMonth,
  resolveFirstSeries,
} from "../../utils/portfolioIntroUtils";
import { buildPortfolioIntroChartOptions } from "../utils/portfolioIntroChart";
import PortfolioIntroStatCard from "./intro/PortfolioIntroStatCard";
import PortfolioIntroLineChartCard from "./intro/PortfolioIntroLineChartCard";
import img14 from "../../assets/img/img14.jpg";
import {AdUnit} from "../../components/AdComponent";

export default function IntroducePortfolio({ model }) {
  // Intro section reads data/navigation from the page model instead of many props.
  const {
    portfolioData,
    portfolioLoading,
    portfolioError,
    guruAvatar,
    allTickers,
    onNavigateTicker,
    showInvestChart = true,
  } = model;

  // 다크 모드 관련 설정
  const isDarkMode = useDarkMode();
  const theme = getChartTheme(isDarkMode);

  const aboutPortfolio = portfolioData?.aboutPortfolio;
  // 페이징 관련 설정
  const [listPage, setListPage] = useState(1);
  const LIST_PAGE_SIZE = 10;

  const return365 = aboutPortfolio?.return365 ?? null;
  const return30 = aboutPortfolio?.return30 ?? null;
  const rank365 = aboutPortfolio?.rank365 ?? null;
  const displayNickname = aboutPortfolio?.nickname;
  const displayLeader = aboutPortfolio?.leader;
  const displayInstitution = aboutPortfolio?.institution;
  const displayComment = aboutPortfolio?.comment || "";
  const allTickerTooltip = "투자자의 보유 종목과 각 종목의 비중 및 변동을 보여줍니다.";

  // API 응답에서 invest/perf 후보 키를 순차 탐색해 실제 시계열 소스를 결정한다.
  const investRawSeries = useMemo(() => {
    return resolveFirstSeries([
      portfolioData?.investAmount,
      portfolioData?.dashboardGuru,
      portfolioData?.dashboardguru,
      portfolioData?.dashboard_guru,
      portfolioData?.dashboard,
      portfolioData?.investamount,
    ], ["investAmount", "investamount", "invest"]);
  }, [portfolioData]);

  const perfRawSeries = useMemo(() => {
    return resolveFirstSeries([
      portfolioData?.perfData,
      portfolioData?.dashboardGuru,
      portfolioData?.dashboardguru,
      portfolioData?.dashboard_guru,
      portfolioData?.dashboard,
      portfolioData?.perfguruwhisper,
      portfolioData?.perfdata,
    ], ["perfguruwhisper", "perfData", "perfdata", "perf"]);
  }, [portfolioData]);

  // 날짜 파싱이 가능한 경우 시간순 정렬로 차트 입력을 안정화한다.
  const investPoints = useMemo(() => {
    const parsed = parseSeries(investRawSeries);
    if (parsed.length === 0) return [];
    const withTimestamps = parsed.map((point) => ({
      ...point,
      ts: parseDateValue(point.date),
    }));
    if (withTimestamps.every((point) => point.ts !== null)) {
      return withTimestamps.sort((a, b) => a.ts - b.ts);
    }
    return parsed;
  }, [investRawSeries]);

  // 월간 성과 시계열도 동일한 정렬 규칙을 적용한다.
  const perfPoints = useMemo(() => {
    const parsed = parseSeries(perfRawSeries);
    if (parsed.length === 0) return [];
    const withTimestamps = parsed.map((point) => ({
      ...point,
      ts: parseDateValue(point.date),
    }));
    if (withTimestamps.every((point) => point.ts !== null)) {
      return withTimestamps.sort((a, b) => a.ts - b.ts);
    }
    return parsed;
  }, [perfRawSeries]);

  // 분기 투자액 차트
  const investChart = useMemo(() => {
    if (investPoints.length === 0) return null;
    const maxValue = Math.max(...investPoints.map((point) => Math.abs(point.value)));
    const unit = maxValue >= 1_000_000_000_000 ? "B" : "M";
    const divisor = unit === "B" ? 1_000_000_000 : 1_000_000;
    const labels = investPoints.map((point) => formatLabel(point.date, "quarter"));
    const values = investPoints.map((point) => {
      const scaled = point.value / divisor;
      const rounded = roundUp(scaled, 2);
      return rounded === null ? 0 : rounded;
    });

    return {
      data: {
        labels,
        datasets: [
          {
            label: "분기 투자액",
            data: values,
            borderColor: theme.dataLine,
            backgroundColor: theme.dataFill,
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 3,
            fill: true,
          },
        ],
      },
      unit,
      latest: investPoints[investPoints.length - 1],
    };
  }, [investPoints, theme]);

  const perfChart = useMemo(() => {
    if (perfPoints.length === 0) return null;
    const labels = perfPoints.map((point) => formatLabel(point.date, "month"));
    const values = perfPoints.map((point) => {
      const rounded = roundUp(point.value, 2);
      return rounded === null ? 0 : rounded;
    });

    return {
      data: {
        labels,
        datasets: [
          {
            label: "월간 성과",
            data: values,
            borderColor: theme.dataLine,
            backgroundColor: theme.dataFill,
            borderWidth: 2,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 3,
            fill: true,
          },
        ],
      },
      latest: perfPoints[perfPoints.length - 1],
    };
  }, [perfPoints, theme]);


  const perfBalance = toNumber(aboutPortfolio?.balance ?? 0);
  const perfDuration = toNumber(aboutPortfolio?.dur ?? null);
  const perfCagr = toNumber(aboutPortfolio?.cagr ?? 0);

  // 요약 수치는 포맷 함수를 통해 카드 표시 형태로 정규화한다.
  const perfBalanceDisplay = useMemo(() => {
    if (!Number.isFinite(perfBalance)) return "-";
    return formatCurrencyPlain(perfBalance, 0);
  }, [perfBalance]);

  const perfDurationDisplay = useMemo(() => {
    if (!Number.isFinite(perfDuration)) return null;
    const rounded = roundUp(perfDuration, 1);
    if (rounded === null) return null;
    return `${rounded.toFixed(1)}년`;
  }, [perfDuration]);

  const perfCagrDisplay = useMemo(() => {
    if (!Number.isFinite(perfCagr)) return "-";
    const normalized = Math.abs(perfCagr) <= 1 ? perfCagr * 100 : perfCagr;
    const rounded = roundUp(normalized, 1);
    if (rounded === null) return "-";
    return `${rounded.toFixed(1)}%`;
  }, [perfCagr]);

  const [profileImageSrc, setProfileImageSrc] = useState(() => guruAvatar || img14);
  const tickerList = useMemo(() => (Array.isArray(allTickers) ? allTickers : []), [allTickers]);
  const {
    paginatedItems: paginatedTickers,
    totalPages: totalTickerPages,
    currentPage: currentTickerPage,
  } = usePagination(tickerList, listPage, LIST_PAGE_SIZE);
  // 종목 리스트가 바뀌면 페이지를 첫 페이지로 복귀시킨다.
  useEffect(() => {
    setListPage(1);
  }, [tickerList.length]);

  // guru 아바타 변경 시 이미지 fallback 상태를 재초기화한다.
  useEffect(() => {
    setProfileImageSrc(guruAvatar || img14);
  }, [guruAvatar]);

  const latestInvestValue = useMemo(() => {
    if (!investChart?.latest) return "-";
    return formatCurrencyShort(investChart.latest.value, investChart.unit);
  }, [investChart]);

  const latestInvestLabel = useMemo(() => {
    if (!investChart?.latest) return "데이터가 없습니다";
    const label = formatYearMonth(investChart.latest.date);
    return label ? `${label} 기준` : "최신 기준";
  }, [investChart]);

  if (portfolioLoading && !portfolioData) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (portfolioError && !portfolioData) {
    return <div className="text-center py-4 text-secondary">{portfolioError}</div>;
  }

  return (
    <div className="p-3 p-lg-4 portfolio-intro portfolio-mobile-swipe-surface">
      <Row className="g-4">
        <Col lg={8}>
          <Row className="g-4 align-items-stretch">
            <Col lg={4}>
              <div className="w-100 portfolio-intro-photo">
                <img
                  src={profileImageSrc}
                  alt={displayLeader}
                  className="img-fluid w-100 h-100 no-copy"
                  style={{ objectFit: "cover" }}
                  onError={() => {
                    // 개별 이미지 실패 시 기본 이미지로 1회 fallback.
                    if (profileImageSrc === img14) return;
                    setProfileImageSrc(img14);
                  }}
                />
              </div>
            </Col>
            <Col lg={8}>
              <div className="h-100 d-flex flex-column">
                <h2 className="portfolio-intro-name">{displayNickname}</h2>
                <h3 className="portfolio-intro-leader">{displayLeader}</h3>
                <div className="portfolio-intro-meta">{displayInstitution}</div>
                <p className="portfolio-intro-comment mb-0">{displayComment || "소개글이 없습니다."}</p>
              </div>
            </Col>
          </Row>

          <Row className="g-3 mt-2">
            <Col md={4}>
              <PortfolioIntroStatCard
                icon="ri-line-chart-line"
                value={formatPercent(toNumber(return365))}
                label="TOP10종목 따라사는 미러포트폴리오 일년수익(YoY)"
                theme={theme}
              />
            </Col>
            <Col md={4}>
              <PortfolioIntroStatCard
                icon="ri-calendar-line"
                value={formatPercent(toNumber(return30))}
                label="TOP10종목 따라사는 미러포트폴리오 한달수익"
                theme={theme}
              />
            </Col>
            <Col md={4}>
              <PortfolioIntroStatCard
                icon="ri-bar-chart-2-line"
                value={formatRank(toNumber(rank365))}
                label="위스퍼 선정 위대한 투자자 순위"
                theme={theme}
              />
            </Col>
          </Row>

          <Row className="g-4 mt-2">
            {showInvestChart && (
              <Col lg={6}>
                <PortfolioIntroLineChartCard
                  title="분기 투자액 추이"
                  value={latestInvestValue}
                  subtexts={[latestInvestLabel]}
                  chartData={investChart?.data}
                  chartOptions={buildPortfolioIntroChartOptions(isDarkMode, "$", investChart?.unit || "", true)}
                />
              </Col>
            )}
            <Col lg={showInvestChart ? 6 : 12}>
              <PortfolioIntroLineChartCard
                title="공시 직후 따라산다면"
                value={`$1,000 -> ${perfBalanceDisplay}`}
                valueClassName="portfolio-intro-card-value-sm"
                subtexts={[
                  perfDurationDisplay ? `${perfDurationDisplay} 동안 검증했어요` : "데이터가 없습니다",
                  `복리 연평균 수익률: ${perfCagrDisplay}`,
                ]}
                chartData={perfChart?.data}
                chartOptions={buildPortfolioIntroChartOptions(isDarkMode, "", "", true)}
              />
            </Col>
          </Row>
        </Col>
        <Col lg={4}>
          <div className="portfolio-intro-card h-100 d-flex flex-column">
            <div className="portfolio-intro-list-header mb-2">
              <div className="d-flex align-items-center gap-1"
                style={{ justifyContent: "space-between" }}
              >
                <div>
                  <span className="portfolio-intro-list-title">전체 종목 리스트</span>
                  <OverlayTrigger
                    trigger={["hover", "focus", "click"]}
                    placement="top"
                    rootClose
                    overlay={
                      <Tooltip>
                        {allTickerTooltip}
                      </Tooltip>}
                  >
                    <button type="button" className="btn btn-link p-0 text-secondary">
                      <i className="ri-question-line"></i>
                    </button>
                  </OverlayTrigger>
                </div>
                <div>
                  <div className="portfolio-intro-list-meta">
                    <span className="portfolio-intro-list-date">기준일: {portfolioData?.period || "-"}</span>
                    <span className="portfolio-intro-list-count">{tickerList.length}개</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="portfolio-intro-list flex-grow-1">
              {tickerList.length ? (
                // 우측 전체 종목 리스트는 페이지 단위로 잘라서 렌더링한다.
                paginatedTickers.map((item, index) => (
                  <div
                    key={`${item.ticker || "ticker"}-${index}`}
                    className={`portfolio-intro-list-item d-flex align-items-center justify-content-between${onNavigateTicker ? " is-clickable" : ""
                      }`}
                    role={onNavigateTicker ? "button" : undefined}
                    tabIndex={onNavigateTicker ? 0 : undefined}
                    onClick={() => {
                      if (onNavigateTicker && item?.ticker) {
                        onNavigateTicker(item.ticker);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (!onNavigateTicker) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (item?.ticker) {
                          onNavigateTicker(item.ticker);
                        }
                      }
                    }}
                  >
                    <div>
                      <div className="portfolio-intro-list-name">{item.name || item.ticker}</div>
                      <div className="portfolio-intro-list-sub">{item.nickname}</div>
                    </div>
                    <div className="portfolio-intro-list-metrics">
                      <div className="portfolio-intro-list-metric-item">
                        <span className="portfolio-intro-list-metric-label">보유비중</span>
                        <span className="portfolio-intro-list-value">{Number(item.percent).toFixed(2)}%</span>
                      </div>
                      <div className="portfolio-intro-list-metric-item">
                        <span className="portfolio-intro-list-metric-label">변화량</span>
                        <span className={`portfolio-intro-list-diff ${Number(item.diff).toFixed(2) >= 0 ? " text-success" : "text-danger"}`}>
                          {Number(item.diff).toFixed(2) >= 0 ? "+" : ""}{Number(item.diff).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-secondary fs-sm">데이터가 없습니다.</div>
              )}
            </div>
            {tickerList.length > 0 && (
              <PaginationControls
                currentPage={currentTickerPage}
                totalPages={totalTickerPages}
                onPrev={() => setListPage((prev) => Math.max(1, prev - 1))}
                onNext={() => setListPage((prev) => Math.min(totalTickerPages, prev + 1))}
                className="mt-3"
              />
            )}
          </div>
        </Col>
      </Row>
      <div className="d-flex justify-content-center my-4">
        <div style={{ width: '100%', maxWidth: '970px' }}>
          <AdUnit key="introduce_3951546463" slot="3951546463" style={{ overflowX: "scroll", overflowY: "hidden" }} />
        </div>
      </div>
    </div>
  );
}
